<template>
    <el-dialog
        :title="rule ? $t('marketing.affiliate.rule.edit_title') : $t('marketing.affiliate.rule.add')"
        :visible="visible"
        width="600px"
        top="6vh"
        append-to-body
        :close-on-click-modal="false"
        @update:visible="(v) => $emit('update:visible', v)"
        @open="onOpen"
    >
        <div data-testid="aff-rule-dialog">
            <el-alert v-if="stale" type="warning" :closable="false" show-icon class="mb-3" :title="$t('marketing.affiliate.err.stale')"></el-alert>

            <!-- 1. scope -->
            <div class="form-group">
                <label class="form-control-label">{{ $t("marketing.affiliate.rule.scope") }}</label>
                <div>
                    <el-radio-group v-model="form.scope" size="small" data-testid="aff-rule-scope" @change="onScope">
                        <el-radio-button v-for="s in scopes" :key="s" :label="s">{{ tk("marketing.affiliate.rule.scope_", s) }}</el-radio-button>
                    </el-radio-group>
                </div>
            </div>

            <!-- 2. target -->
            <div v-if="needsTarget" class="form-group">
                <label class="form-control-label">{{ $t("marketing.affiliate.rule.target") }}</label>
                <el-select
                    v-model="form.scope_id"
                    class="w-100"
                    filterable
                    remote
                    clearable
                    :remote-method="search"
                    :loading="searching"
                    :placeholder="$t('marketing.affiliate.rule.target_placeholder')"
                    data-testid="aff-rule-target"
                    @visible-change="(o) => o && !options.length && search('')"
                >
                    <el-option v-for="o in options" :key="o.id" :value="o.id" :label="o.label"></el-option>
                </el-select>
                <small v-if="errors.scope_id" class="text-danger">{{ errText2(errors.scope_id) }}</small>
            </div>

            <!-- 3. rate type + 4. rate -->
            <div class="row">
                <div class="col-sm-6 form-group">
                    <label class="form-control-label">{{ $t("marketing.affiliate.rule.rate_type") }}</label>
                    <div>
                        <el-radio-group v-model="form.rate_type" size="small" data-testid="aff-rule-type">
                            <el-radio-button label="percent">{{ $t("marketing.affiliate.rule.rate_type_percent") }}</el-radio-button>
                            <el-radio-button label="fixed">{{ $t("marketing.affiliate.rule.rate_type_fixed") }}</el-radio-button>
                        </el-radio-group>
                    </div>
                </div>
                <div class="col-sm-6 form-group">
                    <label class="form-control-label">{{ $t("marketing.affiliate.rule.rate") }} ({{ form.rate_type === "percent" ? "%" : "฿ / " + $t("marketing.affiliate.rule.per_item") }})</label>
                    <el-input-number v-model="form.rate" :min="0" :max="form.rate_type === 'percent' ? 50 : 10000" :precision="2" :step="1" class="w-100" data-testid="aff-rule-rate"></el-input-number>
                    <small class="d-block text-muted">{{ $t("marketing.affiliate.rule.zero_hint") }}</small>
                    <small v-if="errors.rate" class="text-danger">{{ errText2(errors.rate) }}</small>
                </div>
            </div>

            <!-- 5. window -->
            <div class="row">
                <div class="col-sm-6 form-group">
                    <label class="form-control-label">{{ $t("marketing.affiliate.rule.starts_at") }}</label>
                    <el-date-picker v-model="form.starts_local" type="datetime" value-format="yyyy-MM-ddTHH:mm:ss" format="yyyy-MM-dd HH:mm" class="w-100" clearable></el-date-picker>
                </div>
                <div class="col-sm-6 form-group">
                    <label class="form-control-label">{{ $t("marketing.affiliate.rule.ends_at") }}</label>
                    <el-date-picker v-model="form.ends_local" type="datetime" value-format="yyyy-MM-ddTHH:mm:ss" format="yyyy-MM-dd HH:mm" class="w-100" clearable></el-date-picker>
                    <small v-if="errors.ends_at" class="text-danger">{{ errText2(errors.ends_at) }}</small>
                </div>
            </div>

            <!-- 6. note -->
            <div class="form-group mb-0">
                <label class="form-control-label">{{ $t("marketing.affiliate.rule.note") }}</label>
                <el-input v-model="form.note" maxlength="300" show-word-limit></el-input>
            </div>
        </div>
        <span slot="footer">
            <base-button type="secondary" @click="$emit('update:visible', false)">{{ $t("marketing.affiliate.cancel") }}</base-button>
            <base-button type="primary" :loading="saving" data-testid="aff-rule-save" @click="save">{{ $t("marketing.affiliate.save") }}</base-button>
        </span>
    </el-dialog>
</template>

<script>
import { Dialog, RadioGroup, RadioButton, Select, Option, InputNumber, DatePicker, Input, Alert } from "element-ui";
import { AffiliateService, AppCatalogProductService, AppCatalogPromotionBundleService, AppCatalogCategoryService } from "@/resource";
import { apiErrorCode, bkkLocalToIso, isoToBkkLocal } from "@/utils/marketing";
import { RULE_SCOPES, ruleErrors, ruleNeedsTarget, rulePayload } from "@/utils/affiliate";
import affiliateMixin from "./affiliateMixin";

const blank = () => ({ scope: "default", scope_id: null, rate_type: "percent", rate: 10, starts_local: "", ends_local: "", note: "" });
const nameOf = (x) => x.name_th || x.name || x.name_en || x.title || "";

// Rule dialog (Phase 3 spec 2026-10-13 §5.2): scope · target · type · rate ·
// window · note. Targets: products (AppCatalogProductService, as the home
// content picker), bundles (/api/v1/bo/catalog/promotions), categories
// (/seller/categories source).
export default {
    name: "AffiliateRuleDialog",
    components: {
        [Dialog.name]: Dialog,
        [RadioGroup.name]: RadioGroup,
        [RadioButton.name]: RadioButton,
        [Select.name]: Select,
        [Option.name]: Option,
        [InputNumber.name]: InputNumber,
        [DatePicker.name]: DatePicker,
        [Input.name]: Input,
        [Alert.name]: Alert
    },
    mixins: [affiliateMixin],
    props: {
        visible: { type: Boolean, default: false },
        rule: { type: Object, default: null }
    },
    data() {
        return { form: blank(), errors: {}, options: [], searching: false, saving: false, stale: false, scopes: RULE_SCOPES };
    },
    computed: {
        needsTarget() {
            return ruleNeedsTarget(this.form.scope);
        }
    },
    methods: {
        errText2(rule) {
            return this.$t("marketing.affiliate.err." + rule);
        },
        onOpen() {
            this.errors = {};
            this.stale = false;
            const r = this.rule;
            this.form = r
                ? { scope: r.scope, scope_id: r.scope_id, rate_type: r.rate_type, rate: Number(r.rate), starts_local: isoToBkkLocal(r.starts_at), ends_local: isoToBkkLocal(r.ends_at), note: r.note || "" }
                : blank();
            this.options = r && r.scope_id ? [{ id: r.scope_id, label: `#${r.scope_id} ${r.target_name || ""}`.trim() }] : [];
        },
        onScope() {
            this.form.scope_id = null;
            this.options = [];
        },
        async search(q) {
            this.searching = true;
            try {
                let list = [];
                if (this.form.scope === "product") {
                    const params = { page: 1, limit: 30, app_enable: 1 };
                    if (q) params.keyword = q;
                    const res = await AppCatalogProductService.list(params);
                    list = ((res.data && res.data.data) || []).map((p) => ({ id: p.id, label: `${p.code || p.id} - ${nameOf(p)}` }));
                } else if (this.form.scope === "promotion") {
                    const res = await AppCatalogPromotionBundleService.list(q ? { q, page: 1, limit: 50 } : { page: 1, limit: 50 });
                    list = ((res.data && res.data.data) || []).map((p) => ({ id: p.id, label: `#${p.id} ${nameOf(p)}` }));
                } else if (this.form.scope === "category") {
                    const res = await AppCatalogCategoryService.list({});
                    list = ((res.data && res.data.data) || []).map((c) => ({ id: c.id, label: `#${c.id} ${nameOf(c)}` }));
                }
                const s = String(q || "").toLowerCase();
                this.options = this.form.scope === "product" || !s ? list : list.filter((o) => o.label.toLowerCase().indexOf(s) !== -1);
            } catch (e) {
                this.options = [];
            } finally {
                this.searching = false;
            }
        },
        async save() {
            const f = { ...this.form, starts_at: bkkLocalToIso(this.form.starts_local), ends_at: bkkLocalToIso(this.form.ends_local) };
            this.errors = ruleErrors(f);
            if (Object.keys(this.errors).length) return;
            const payload = rulePayload(f);
            this.saving = true;
            try {
                if (this.rule) await AffiliateService.updateRule(this.rule.id, { ...payload, expected_updated_at: this.rule.updated_at || null });
                else await AffiliateService.createRule(payload);
                this.notifyOk();
                this.$emit("saved");
                this.$emit("update:visible", false);
            } catch (err) {
                const code = apiErrorCode(err);
                if (code === "stale") this.stale = true;
                else if (code === "target_not_found") this.errors = { scope_id: "target_not_found" };
                else if (code === "invalid_body") {
                    const e = {};
                    ((err.response.data && err.response.data.errors) || []).forEach((x) => (e[x.field] = x.rule));
                    this.errors = e;
                    this.showError(err);
                } else this.showError(err);
            } finally {
                this.saving = false;
            }
        }
    }
};
</script>
